import { useEffect, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
    Shield,
    Users,
    Ban,
    Activity,
    Brain,
    Siren,
    ArrowLeft,
    ExternalLink,
} from "lucide-react";
import KpiCard from "@/components/dashboard/KpiCard";
import EventFeed from "@/components/dashboard/EventFeed";
import {
    overview,
    listEvents,
    listAlerts,
    timeseries,
} from "@/lib/api";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
} from "recharts";

export default function Overview() {
    const { liveEvents = [] } = useOutletContext() || {};
    const [stats, setStats] = useState(null);
    const [events, setEvents] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [series, setSeries] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            try {
                const [o, e, a, t] = await Promise.all([
                    overview(),
                    listEvents({ limit: 30 }),
                    listAlerts(true),
                    timeseries(7),
                ]);
                if (!mounted) return;
                setStats(o);
                setEvents(e.events || []);
                setAlerts(a.alerts || []);
                setSeries(t.series || []);
            } catch (_) {}
            if (mounted) setLoading(false);
        };
        load();
        const interval = setInterval(load, 30000);
        return () => {
            mounted = false;
            clearInterval(interval);
        };
    }, []);

    // Merge live events on top
    const merged = [...liveEvents.filter((m) => m.type === "event").map((m) => m.data), ...events]
        .filter((e, idx, arr) => arr.findIndex((x) => x.id === e.id) === idx)
        .slice(0, 50);

    return (
        <div className="space-y-4 md:space-y-6">
            {/* Page header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
                        لوحة التحكم
                    </h1>
                    <p className="mt-1 text-sm text-muted-foreground">
                        نظرة عامة على حالة حماية جميع المجموعات
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => navigate("/groups")}
                    data-testid="overview-manage-groups-button"
                >
                    إدارة المجموعات
                    <ArrowLeft className="h-4 w-4" />
                </Button>
            </div>

            {/* KPI Row */}
            <div
                className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4"
                data-testid="overview-kpi-row"
            >
                <KpiCard
                    title="المجموعات المحمية"
                    value={stats?.total_groups ?? 0}
                    icon={Users}
                    tone="info"
                    loading={loading}
                    hint={`${stats?.active_groups ?? 0} نشطة`}
                    testId="kpi-total-groups"
                />
                <KpiCard
                    title="تنبيهات معلّقة"
                    value={stats?.pending_alerts ?? 0}
                    icon={Siren}
                    tone={
                        (stats?.pending_alerts ?? 0) > 0 ? "critical" : "safe"
                    }
                    loading={loading}
                    hint="تحتاج مراجعة"
                    testId="kpi-pending-alerts"
                />
                <KpiCard
                    title="أحداث اليوم"
                    value={stats?.today_events ?? 0}
                    icon={Activity}
                    tone="info"
                    loading={loading}
                    hint={`${stats?.today_bans ?? 0} حظر`}
                    testId="kpi-today-events"
                />
                <KpiCard
                    title="تصيدات AI"
                    value={stats?.ai_catches ?? 0}
                    icon={Brain}
                    tone="warning"
                    loading={loading}
                    hint="جميع المجموعات"
                    testId="kpi-ai-catches"
                />
            </div>

            {/* Chart + Live Feed */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                <Card className="xl:col-span-8 bg-card/60 backdrop-blur">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <div>
                            <CardTitle className="text-base md:text-lg">
                                حركة الأسبوع الماضي
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                                الإنضمام/السبام/الحظر/AI
                            </p>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                        {loading ? (
                            <Skeleton className="h-[280px] w-full" />
                        ) : (
                            <div className="h-[280px]" dir="ltr">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={series}>
                                        <CartesianGrid
                                            strokeOpacity={0.15}
                                            vertical={false}
                                        />
                                        <XAxis
                                            dataKey="day"
                                            tick={{
                                                fill: "hsl(var(--muted-foreground))",
                                                fontSize: 11,
                                            }}
                                        />
                                        <YAxis
                                            tick={{
                                                fill: "hsl(var(--muted-foreground))",
                                                fontSize: 11,
                                            }}
                                        />
                                        <Tooltip
                                            contentStyle={{
                                                background:
                                                    "hsl(var(--card))",
                                                border:
                                                    "1px solid hsl(var(--border))",
                                                borderRadius: 8,
                                                fontSize: 12,
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{ fontSize: 11 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="joins"
                                            stroke="hsl(188 92% 45%)"
                                            strokeWidth={2}
                                            dot={false}
                                            name="Joins"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="spam"
                                            stroke="hsl(38 92% 55%)"
                                            strokeWidth={2}
                                            dot={false}
                                            name="Spam"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="bans"
                                            stroke="hsl(0 72% 52%)"
                                            strokeWidth={2}
                                            dot={false}
                                            name="Bans"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="ai_catches"
                                            stroke="hsl(158 72% 38%)"
                                            strokeWidth={2}
                                            dot={false}
                                            name="AI"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card
                    className="xl:col-span-4 bg-card/60 backdrop-blur"
                    data-testid="overview-live-feed"
                >
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-base md:text-lg">
                                الأحداث المباشرة
                            </CardTitle>
                            <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                <span
                                    className="live-dot"
                                    style={{
                                        backgroundColor:
                                            "hsl(188 92% 45%)",
                                    }}
                                />
                                Live
                            </span>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <EventFeed events={merged} height="h-[280px]" />
                    </CardContent>
                </Card>
            </div>

            {/* Alerts */}
            <Card className="bg-card/60 backdrop-blur" data-testid="overview-alerts-table">
                <CardHeader>
                    <CardTitle className="text-base md:text-lg">
                        آخر التنبيهات الحرجة
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {alerts.length === 0 ? (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                            ✅ لا توجد تنبيهات غير مؤكدة.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {alerts.slice(0, 6).map((a) => (
                                <div
                                    key={a.id}
                                    className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2"
                                >
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium">
                                            {a.title}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {a.message}
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => navigate("/alerts")}
                                        className="shrink-0 gap-1"
                                    >
                                        <ExternalLink className="h-3 w-3" />
                                        عرض
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
